import { BasicProcessPhase, ConditionStatus } from "../../utils/enums";
import { ITransferMetadataUtils } from "./TransferMetaData";

// CurrentPhase interface
export interface CurrentPhase {
  readonly index: BasicProcessPhase;
  readonly name: string;
  readonly description: string;
  readonly data: any[];
  readonly errors: SafeObjectErrorLog[];
}

export const CurrentPhaseUtils = {
  create({
    index,
    name,
    description,
    data,
    errors,
  }: {
    index: BasicProcessPhase;
    name: string;
    description: string;
    data: any[];
    errors: SafeObjectErrorLog[];
  }): CurrentPhase {
    return {
      index,
      name,
      description,
      data,
      errors,
    };
  },

  empty(): CurrentPhase {
    return {
      index: BasicProcessPhase.NON_EXISTS,
      name: "",
      description: "",
      data: [],
      errors: [],
    };
  },

  fromMap(map: Record<string, any>): CurrentPhase {
    try {
      const arr: any[] = [];
      const data = map["data"];
      if (data && data !== "") {
        for (const item of Array.isArray(data) ? data : [data]) {
          if (typeof item === "string") {
            continue;
          }

          if (Array.isArray(item)) {
            const hasTransferStatus = item.some(
              (d: any) => typeof d !== "string" && d["transfer_status"] != null
            );
            if (hasTransferStatus) {
              arr.push(ITransferMetadataUtils.fromMap(item as Record<string, any>[]));
            } else {
              for (const subItem of item) {
                if (typeof subItem === "string") continue;
                if (subItem["user"] != null) {
                  arr.push(UserVoteMetadataUtils.fromMap(subItem));
                } else if (subItem["members"] != null) {
                  arr.push(VoteMetadataUtils.fromMap(subItem));
                } else if (subItem["policyID"] != null) {
                  arr.push(PolicyMetaDataUtils.fromMap(subItem));
                }
              }
            }
          } else if (typeof item === "object" && item != null) {
            console.log("i is not List");
          }
        }
      }

      const indexValue = parseInt(String(map["index"]));
      const validIndex = !isNaN(indexValue) ? Math.min(Math.max(indexValue, 0), 5) : 0;
      const phaseValues = Object.values(BasicProcessPhase);

      return {
        index: phaseValues[validIndex] as BasicProcessPhase,
        name: String(map["name"] ?? ""),
        description: String(map["description"] ?? ""),
        data: arr,
        errors:
          Array.isArray(map["errors"]) && map["errors"].length > 0
            ? map["errors"].map((error: any) => SafeObjectErrorLogUtils.fromMap(error))
            : [],
      };
    } catch (e) {
      console.error(e);
      return CurrentPhaseUtils.empty();
    }
  },

  toMap(phase: CurrentPhase): Record<string, any> {
    return {
      index: phase.index,
      name: phase.name,
      description: phase.description,
    };
  },

  toJson(phase: CurrentPhase): string {
    return JSON.stringify(CurrentPhaseUtils.toMap(phase));
  },
};

// SafeObjectErrorLog interface
export interface SafeObjectErrorLog {
  message: string;
  conditionName: string;
  conditionClass: string;
  conditionId: string;
}

export const SafeObjectErrorLogUtils = {
  create({
    message,
    conditionName,
    conditionClass,
    conditionId,
  }: {
    message: string;
    conditionName: string;
    conditionClass: string;
    conditionId: string;
  }): SafeObjectErrorLog {
    return {
      message,
      conditionName,
      conditionClass,
      conditionId,
    };
  },

  empty(): SafeObjectErrorLog {
    return {
      conditionClass: "",
      conditionId: "",
      conditionName: "",
      message: "",
    };
  },

  copyWith(
    errorLog: SafeObjectErrorLog,
    {
      message,
      conditionName,
      conditionClass,
      conditionId,
    }: {
      message?: string;
      conditionName?: string;
      conditionClass?: string;
      conditionId?: string;
    }
  ): SafeObjectErrorLog {
    return {
      message: message ?? errorLog.message,
      conditionName: conditionName ?? errorLog.conditionName,
      conditionClass: conditionClass ?? errorLog.conditionClass,
      conditionId: conditionId ?? errorLog.conditionId,
    };
  },

  toMap(errorLog: SafeObjectErrorLog): Record<string, any> {
    return {
      message: errorLog.message,
      conditionName: errorLog.conditionName,
      conditionClass: errorLog.conditionClass,
      conditionId: errorLog.conditionId,
    };
  },

  fromMap(map: Record<string, any>): SafeObjectErrorLog {
    return {
      message: map["message"] ?? "",
      conditionName: map["condition_name"] ?? "",
      conditionClass: map["condition_class"] ?? "",
      conditionId: map["condition_id"] ?? "",
    };
  },

  toJson(errorLog: SafeObjectErrorLog): string {
    return JSON.stringify(SafeObjectErrorLogUtils.toMap(errorLog));
  },

  fromJson(source: string): SafeObjectErrorLog {
    return SafeObjectErrorLogUtils.fromMap(JSON.parse(source));
  },

  toString(errorLog: SafeObjectErrorLog): string {
    return `SafeObjectErrorLog(message: ${errorLog.message}, conditionName: ${errorLog.conditionName}, conditionClass: ${errorLog.conditionClass}, conditionId: ${errorLog.conditionId})`;
  },

  equals(errorLog1: SafeObjectErrorLog, errorLog2: SafeObjectErrorLog): boolean {
    return (
      errorLog1.message === errorLog2.message &&
      errorLog1.conditionName === errorLog2.conditionName &&
      errorLog1.conditionClass === errorLog2.conditionClass &&
      errorLog1.conditionId === errorLog2.conditionId
    );
  },
};

// PolicyMetaData interface
export interface PolicyMetaData {
  readonly policyID: string;
  readonly conditionStatus: ConditionStatus;
  readonly conditionClass: string;
}

export const PolicyMetaDataUtils = {
  create(
    policyID: string,
    conditionStatus: ConditionStatus,
    conditionClass: string
  ): PolicyMetaData {
    return {
      policyID,
      conditionStatus,
      conditionClass,
    };
  },

  empty(): PolicyMetaData {
    return {
      policyID: "",
      conditionStatus: ConditionStatus.NON_RELEVANT,
      conditionClass: "",
    };
  },

  fromMap(map: Record<string, any>): PolicyMetaData {
    try {
      const conditionStatusIndex =
        map["conditionStatus"] != null && !isNaN(parseInt(map["conditionStatus"]))
          ? parseInt(map["conditionStatus"])
          : 2;
      const conditionStatusValues = Object.values(ConditionStatus);
      const conditionStatus =
        conditionStatusValues[conditionStatusIndex] || ConditionStatus.NON_RELEVANT;

      return {
        policyID: (map["policyID"] ?? "").toString(),
        conditionStatus,
        conditionClass: (map["conditionClass"] ?? "").toString(),
      };
    } catch (e) {
      console.error(e);
      return PolicyMetaDataUtils.empty();
    }
  },

  toMap(policyData: PolicyMetaData): Record<string, any> {
    return {
      policyID: policyData.policyID,
      conditionStatus: policyData.conditionStatus,
      conditionClass: policyData.conditionClass,
    };
  },

  toJson(policyData: PolicyMetaData): string {
    return JSON.stringify(PolicyMetaDataUtils.toMap(policyData));
  },
};

// UserVoteMetadata interface
export interface UserVoteMetadata {
  user: string;
  vote: string;
  voteSIGNATURE: string;
}

export const UserVoteMetadataUtils = {
  create({
    user,
    vote,
    voteSIGNATURE,
  }: {
    user: string;
    vote: string;
    voteSIGNATURE: string;
  }): UserVoteMetadata {
    return {
      user,
      vote,
      voteSIGNATURE,
    };
  },

  empty(): UserVoteMetadata {
    return { user: "", vote: "", voteSIGNATURE: "" };
  },

  fromMap(map: Record<string, any>): UserVoteMetadata {
    try {
      const tempFix = (map["user"] ?? "[]").toString().replaceAll("[", "").replaceAll("]", "");
      return {
        user: tempFix,
        vote: (map["vote"] ?? "").toString(),
        voteSIGNATURE: (map["voteSIGNATURE"] ?? "").toString(),
      };
    } catch (e) {
      console.error(e);
      return UserVoteMetadataUtils.empty();
    }
  },

  toMap(userVote: UserVoteMetadata): Record<string, any> {
    return {
      user: userVote.user.toString(),
      vote: userVote.vote,
      voteSIGNATURE: userVote.voteSIGNATURE,
    };
  },

  toJson(userVote: UserVoteMetadata): string {
    return JSON.stringify(UserVoteMetadataUtils.toMap(userVote));
  },

  copyWith(
    userVote: UserVoteMetadata,
    {
      user,
      vote,
      voteSIGNATURE,
    }: {
      user?: string;
      vote?: string;
      voteSIGNATURE?: string;
    }
  ): UserVoteMetadata {
    return {
      user: user ?? userVote.user,
      vote: vote ?? userVote.vote,
      voteSIGNATURE: voteSIGNATURE ?? userVote.voteSIGNATURE,
    };
  },

  copyWithFromLocalVote(
    userVote: UserVoteMetadata,
    { localVote }: { localVote: LocalVote }
  ): UserVoteMetadata {
    return {
      user: localVote.user,
      vote: localVote.vote,
      voteSIGNATURE: localVote.voteSIGNATURE,
    };
  },
};

// VoteMetadata interface
export interface VoteMetadata {
  group: string;
  members: number;
  yes: number;
  no: number;
}

export const VoteMetadataUtils = {
  create({
    group,
    members,
    yes,
    no,
  }: {
    group: string;
    members: number;
    yes: number;
    no: number;
  }): VoteMetadata {
    return {
      group,
      members,
      yes,
      no,
    };
  },

  empty(): VoteMetadata {
    return { group: "", members: 0, yes: 0, no: 0 };
  },

  fromMap(map: Record<string, any>): VoteMetadata {
    return {
      group: map["group"] as string,
      members: Number.isNaN(parseInt(map["members"])) ? 0 : parseInt(map["members"].toString()),
      yes: Number.isNaN(parseInt(map["yes"])) ? 0 : parseInt(map["yes"].toString()),
      no: Number.isNaN(parseInt(map["no"])) ? 0 : parseInt(map["no"].toString()),
    };
  },

  toMap(voteMetadata: VoteMetadata): Record<string, any> {
    return {
      group: voteMetadata.group,
      members: voteMetadata.members,
      yes: voteMetadata.yes.toString(),
      no: voteMetadata.no.toString(),
    };
  },

  toJson(voteMetadata: VoteMetadata): string {
    return JSON.stringify(VoteMetadataUtils.toMap(voteMetadata));
  },

  copyWith(
    voteMetadata: VoteMetadata,
    {
      group,
      members,
      yes,
      no,
    }: {
      group?: string;
      members?: number;
      yes?: number;
      no?: number;
    }
  ): VoteMetadata {
    return {
      group: group ?? voteMetadata.group,
      members: members ?? voteMetadata.members,
      yes: yes ?? voteMetadata.yes,
      no: no ?? voteMetadata.no,
    };
  },

  toString(voteMetadata: VoteMetadata): string {
    return `VoteMetadata(group: ${voteMetadata.group}, members: ${voteMetadata.members}, yes: ${voteMetadata.yes}, no: ${voteMetadata.no})`;
  },

  equals(vote1: VoteMetadata, vote2: VoteMetadata): boolean {
    return (
      vote1.group === vote2.group &&
      vote1.members === vote2.members &&
      vote1.yes === vote2.yes &&
      vote1.no === vote2.no
    );
  },
};

// LocalVote interface (extends IUserVoteMetadata)
export interface LocalVote extends UserVoteMetadata {
  readonly safeObjectSignature: string;
}

export const LocalVoteUtils = {
  create(
    safeObjectSignature: string,
    vote: string,
    user: string,
    voteSIGNATURE: string
  ): LocalVote {
    return {
      safeObjectSignature,
      vote,
      user,
      voteSIGNATURE,
    };
  },

  copyWith(
    localVote: LocalVote,
    { safeObjectSignature, vote, user, voteSIGNATURE }: Partial<LocalVote>
  ): LocalVote {
    return {
      safeObjectSignature: safeObjectSignature ?? localVote.safeObjectSignature,
      vote: vote ?? localVote.vote,
      user: user ?? localVote.user,
      voteSIGNATURE: voteSIGNATURE ?? localVote.voteSIGNATURE,
    };
  },

  toMap(localVote: LocalVote): Record<string, any> {
    const map = { ...UserVoteMetadataUtils.toMap(localVote) };
    map["safeObjectSignature"] = localVote.safeObjectSignature;
    return map;
  },

  toJson(localVote: LocalVote): string {
    return JSON.stringify(LocalVoteUtils.toMap(localVote));
  },

  fromMap(map: Record<string, any>): LocalVote {
    return {
      safeObjectSignature: map["safeObjectSignature"] || "",
      vote: map["vote"] || "",
      user: map["user"] || "",
      voteSIGNATURE: map["voteSIGNATURE"] || "",
    };
  },
};
